import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	useController,
	useForm,
} from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
	Form,
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import {
	useGetFieldOptionRegionDetailQuery,
	useGetFieldOptionRegionListQuery,
	useUpdateFieldOptionRegionDetailStatusMutation,
} from '@/api/field-option/fieldOption';
import dayjs from 'dayjs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { FIELD_UNIT_STATUS } from '@/common/constants/common';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
	title?: string;
	type?: number;
}
export default function RegionStatusPage(props: Props) {
	const { title, type } = props;
	const { fieldId, districtId, subDistrictId, ecvId, buildId } = useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [updateFieldOptionRegionDetailStatus] =
		useUpdateFieldOptionRegionDetailStatusMutation();

	const { data: districtDetails } = useGetFieldOptionRegionDetailQuery(
		districtId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 1 && !districtId,
		}
	);
	const { data: subDistrictDetails } = useGetFieldOptionRegionDetailQuery(
		subDistrictId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 2 && !subDistrictId,
		}
	);
	const { data: ecvDetails } = useGetFieldOptionRegionDetailQuery(ecvId || '', {
		refetchOnMountOrArgChange: true,
		skip: type !== 3 && !ecvId,
	});
	const { data: buildDetails } = useGetFieldOptionRegionDetailQuery(
		buildId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 3 && !buildId,
		}
	);

	const defaultValues: DefaultValues<any> = {};
	const methods = useForm<any>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFieldOptionRegionListQuery(
			{
				pagination: { page: 1, size: 99999 },
				searchString: '',
				sortings: [],
				type: type ?? 0,
				parentId:
					type === 1
						? districtId
						: type === 2
						? subDistrictId
						: type === 3
						? ecvId
						: type === 4
						? buildId
						: '',
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	const fieldList = useController({
		name: 'fieldList',
		control: methods.control,
	});

	useEffect(() => {
		if (propertySummary) {
			if (propertySummary?.data?.items) {
				methods.setValue(
					'fieldList',
					(propertySummary?.data?.items || []).map((item: any) => {
						return { ...item, status: item.status ? true : false };
					})
				);
			}
		}
	}, [propertySummary]);

	const columns: GridColDef[] = [
		{
			field: 'code',
			headerName: t('field.code'),
			flex: 20,
		},
		{
			field: 'nameEn',
			headerName: t('field.en'),
			flex: 20,
		},
		{
			field: 'nameZh',
			headerName: t('field.zh'),
			flex: 20,
		},

		{
			field: 'updatedAt',
			headerName: t('field.lastUpdated'),
			flex: 20,
		},
		{
			field: 'status',
			headerName: t('field.status'),
			flex: 40,
			renderCell: (params: any) => {
				const allRows = methods.watch('fieldList') || [];
				const correctIndex = allRows.findIndex(
					(x: any) => x.id === params.row.id
				);

				return (
					<AppRadioGroup
						control={methods.control}
						name={`fieldList[${correctIndex}].status`}
						row
						options={FIELD_UNIT_STATUS}
					/>
				);
			},
		},
	];

	const handleSubmit = async () => {
		const response = await updateFieldOptionRegionDetailStatus({
			updateStatusDTOList: methods.getValues('fieldList'),
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFieledUnitStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				type === 1
					? `/field-management/field-management-list/region/${fieldId}/district/${districtId}`
					: type === 2
					? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}`
					: type === 3
					? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}`
					: type === 4
					? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}/build/${buildId}`
					: `/field-management/field-management-list/region/${fieldId}`,
				{ state: { refresh: true } }
			);
		}
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{type === 1
						? `${t('field.manageStatus')} (District (${
								districtDetails?.data.nameEn
						  }))`
						: type === 2
						? `${t('field.manageStatus')} (Sub-district (${
								subDistrictDetails?.data.nameEn
						  }))`
						: type === 3
						? `${t('field.manageStatus')} (Estate / Court / Village (${
								ecvDetails?.data.nameEn
						  }))`
						: type === 4
						? `${t('field.manageStatus')} (Building / Block (${
								buildDetails?.data.nameEn
						  }))`
						: t('drawerListItems.regionStatus')}
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<Box marginTop={2}>
							<LocalDataGrid
								rows={transformResponse(methods.getValues('fieldList')) ?? []}
								columns={columns}
								getRowId={(row) => row.id}
								pagination={false}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
				<Button
					onClick={() => {
						navigate(
							type === 1
								? `/field-management/field-management-list/region/${fieldId}/district/${districtId}`
								: type === 2
								? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}`
								: type === 3
								? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}`
								: type === 4
								? `/field-management/field-management-list/region/${fieldId}/district/${districtId}/sub-district/${subDistrictId}/ecv/${ecvId}/build/${buildId}`
								: `/field-management/field-management-list/region/${fieldId}`
						);
					}}
				>
					{t('button.cancel')}
				</Button>
				<Button type='submit' variant='contained' onClick={handleSubmit}>
					{t('button.complete')}
				</Button>
			</Box>
		</>
	);
}
